import capitalize from 'lodash/capitalize';
import words from 'lodash/words';

import { attributeDisplayNames } from './constants';
import {
  NormalizedEventAttributes,
  NormalizedEventAttributeValue,
} from './types';

const attrsToCapitalize: Record<string, string> = attributeDisplayNames.reduce(
  (result, attr) => ({
    ...result,
    [attr.toLowerCase()]: attr,
  }),
  {}
);

export const attrNameToDisplayName = (attr: string | number): string => {
  const _words = words(String(attr));
  const mapped = _words
    .map((word) => {
      const mapped = word != null && attrsToCapitalize[word.toLowerCase()];
      if (mapped) {
        return mapped;
      }
      return word ? capitalize(word) : '';
    })
    .filter(Boolean);
  return mapped.join(' ');
};

export const attrToPageTitle = (attr: string | number): string => {
  return attrNameToDisplayName(attr).replace(/ page$/gi, '');
};

export const onlyValidAttributes = (
  obj?: Record<string, unknown>
): NormalizedEventAttributes => {
  if (!obj) {
    return {};
  }
  return Object.entries(obj).reduce(
    (acc, [key, value]) => (value != null ? { ...acc, [key]: value } : acc),
    {}
  );
};

type KeyValSerializer = (keyVal: {
  key: string;
  value: unknown;
}) => NormalizedEventAttributeValue | undefined;

type SerializeObjectArgs = {
  obj?: Record<string, unknown>;
  keySerializer: KeyValSerializer;
  valueSerializer: KeyValSerializer;
};

type FindAncestorArgs = {
  // The element from which the search will start. If the predicate returns true for this element, it will be returned
  element: Element;
  // When this returns true, that element is returned
  predicate: (el: Element) => boolean | undefined | null;
  // maxLevelUp how many levels to go up
  maxLevelUp?: number;
};
// Works like `element.closest` function, but is much faster (and analytics is a performance-critical aspect)
export const findAncestor = ({
  element,
  predicate,
  maxLevelUp = 10_000,
}: FindAncestorArgs): Element | undefined => {
  let curr: Element | null = element;
  while (curr && maxLevelUp > 0) {
    if (predicate(curr)) {
      return curr;
    }
    maxLevelUp--;
    curr = curr.parentElement;
  }
  return undefined;
};

export const serializeObject = ({
  obj,
  keySerializer,
  valueSerializer,
}: SerializeObjectArgs): NormalizedEventAttributes => {
  const result: NormalizedEventAttributes = {};
  if (!obj || typeof obj !== 'object') {
    return result;
  }
  for (const key in obj) {
    const value = obj[key];
    const serializedKey = keySerializer({ key, value });
    if (serializedKey == null) {
      continue;
    }
    const serializedValue = valueSerializer({ key, value });
    if (serializedValue == null) {
      continue;
    }
    result[serializedKey] = serializedValue;
  }
  return result;
};
